import { type FormEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

interface MissingMarginsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onApply: (marginPercentage: number) => Promise<void>;
  missingCount: number;
  loading: boolean;
}

function parseMargin(value: string): number | null {
  if (value.trim() === '') return null;

  const parsed = Number(value.replace(',', '.'));
  return Number.isFinite(parsed) && parsed >= 0 && parsed <= 100
    ? parsed
    : null;
}

export function MissingMarginsDialog({
  open,
  onOpenChange,
  onApply,
  missingCount,
  loading,
}: MissingMarginsDialogProps) {
  const { t } = useTranslation();
  const [value, setValue] = useState('');
  const margin = parseMargin(value);

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (margin === null || loading) return;

    await onApply(margin);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!loading) onOpenChange(nextOpen);
      }}
    >
      <DialogContent className="sm:max-w-md">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle className="text-center text-lg font-semibold">
              {t('components.deal_control.missing_margins_dialog_title')}
            </DialogTitle>
          </DialogHeader>

          <p className="py-4 text-sm text-muted-foreground">
            {t('components.deal_control.missing_margins_dialog_description', {
              count: missingCount,
            })}
          </p>

          <div className="space-y-2 pb-6">
            <Label htmlFor="missing-margin-percentage">
              {t('components.deal_control.missing_margins_field_label')}
            </Label>
            <div className="relative">
              <Input
                id="missing-margin-percentage"
                value={value}
                onChange={(event) => setValue(event.target.value)}
                inputMode="decimal"
                autoComplete="off"
                autoFocus
                disabled={loading}
                className="h-10 pr-8"
                aria-invalid={value !== '' && margin === null}
              />
              <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">
                %
              </span>
            </div>
            {value !== '' && margin === null ? (
              <p className="text-sm text-destructive">
                {t('components.deal_control.missing_margins_invalid')}
              </p>
            ) : null}
          </div>

          <DialogFooter className="flex gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={loading}
            >
              {t('general.cancel')}
            </Button>
            <Button type="submit" disabled={margin === null || loading}>
              {loading
                ? t('general.saving')
                : t('components.deal_control.apply_margin_and_release')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
